/**
 * apps/automations-portal/src/features/auth/AuthProvider.tsx
 *
 * Firebase auth context for Hattie Automations. Unlike apps/attune-web
 * (anonymous-first, upgrade-in-place) this portal has no anonymous mode —
 * it is shown only to people who already have, or are being given, an
 * account. Google sign-in uses signInWithPopup (not linkWithPopup): there is
 * no anonymous session to preserve here.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  idToken: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Completes a redirect-based sign-in when the user lands back here.
    // onAuthStateChanged fires on its own afterwards, so this only needs to
    // surface an error the redirect itself produced — an unauthorised domain,
    // for instance, fails here rather than in the popup path.
    getRedirectResult(auth).catch((err: unknown) => {
      const code = (err as { code?: string }).code ?? 'unknown';
      // eslint-disable-next-line no-console
      console.error('[auth] redirect sign-in failed', code, err);
    });

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (nextUser) {
        setIdToken(await nextUser.getIdToken());
      } else {
        setIdToken(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  /**
   * Popup first, redirect as a fallback.
   *
   * A popup keeps the page state, which is nicer — but browsers block popups
   * routinely (strict privacy settings, embedded webviews, some corporate
   * profiles), and when they do, the popup call fails in a way that looks
   * identical to "Google rejected you". Falling back to a full-page redirect
   * means a blocked popup is an inconvenience rather than a dead end.
   */
  const signInWithGoogle = useCallback(async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/operation-not-supported-in-this-environment'
      ) {
        await signInWithRedirect(auth, new GoogleAuthProvider());
        return;
      }
      throw err;
    }
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, idToken, signInWithGoogle, signInWithEmail, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
